/**
 * O "criar conta" da página de entrar (A039).
 *
 * Numa marca sem rede de profissionais, é a linha de sempre: "Ainda não tem
 * conta? Criar conta". Numa marca COM rede (`PARTNER_PROVIDERS`), a mesma
 * porta recebe dois tipos de pessoa, e a linha única mandava a profissional
 * para o cadastro de gestante. Aqui vira uma seção com os dois caminhos:
 * paciente vai para o cadastro; profissional vai para uma página que explica
 * que o acesso nasce de um convite da marca.
 *
 * A marca pode preferir que a porta fale só com pacientes mesmo tendo rede
 * (`appUiPreferences.loginOrientaSoPacientes`) — aí volta a linha de sempre.
 *
 * Na marca de porta fechada (`cadastroSoComLink`), o cadastro avulso não
 * existe: a seção só diz que o cadastro é pelo link da empresa. Até
 * 06/10/2026 havia aqui um campo para digitar o código da empresa; saiu
 * (A070) — quem tem o link toca nele, quem vê um cartaz usa o QR, e quem não
 * tem nenhum dos dois recebe convite do RH. O caminho de profissional não
 * muda.
 *
 * Desde a A082 tem o desenho do cadastro: a linha de baixo em 12 px cinza com
 * o link em negrito (como o "Já possui cadastro? Fazer login" de lá), e os
 * caminhos com a borda clara e o raio do kit.
 */
import type { CSSProperties } from 'react';
import { IconChevronRight } from '@tabler/icons-react';
import { Link, useNavigate } from 'react-router-dom';
import { hasBrandFeature } from '@eva/schemas';
import { useBrand } from '@eva/domain';
import { marcarDirecao, navegarCom } from '../cadastro/transicao';

/** A página de entrar desta marca orienta também o cadastro de profissional? */
export function orientaProfissionais(brand: { enabledFeatures?: unknown; appUiPreferences?: unknown }): boolean {
  const temRede = hasBrandFeature(brand.enabledFeatures as any, 'PARTNER_PROVIDERS');
  const soPacientes = Boolean((brand.appUiPreferences as any)?.loginOrientaSoPacientes);
  return temRede && !soPacientes;
}

export function CriarConta() {
  const { brand } = useBrand();
  const navigate = useNavigate();
  const comProfissionais = orientaProfissionais(brand);
  const portaFechada = !!brand.cadastroSoComLink;

  if (portaFechada) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <p style={{ ...LINHA, lineHeight: 1.5 }}>
          O cadastro em {brand.name} é pelo link que a sua empresa enviou. Não recebeu? Fale com o RH da sua empresa.
        </p>
        {comProfissionais && <Caminho pergunta="Quer atender como profissional?" onClick={() => navigate('/cadastro/profissional')} />}
      </div>
    );
  }

  if (!comProfissionais) {
    return (
      <p style={LINHA}>
        Ainda não tem conta?{' '}
        <Link to="/cadastro/email" viewTransition onClick={() => marcarDirecao('fusao')} style={LINK}>
          Criar conta
        </Link>
      </p>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <p style={{ ...LINHA, marginBottom: 4 }}>Ainda não tem conta?</p>
      <Caminho pergunta="Quer acompanhar ou se tornar usuária(o)?" onClick={() => navegarCom(navigate, '/cadastro/email', 'fusao')} />
      <Caminho pergunta="Quer atender como profissional?" onClick={() => navigate('/cadastro/profissional')} />
    </div>
  );
}

const LINHA: CSSProperties = { margin: 0, textAlign: 'center', fontSize: 12, color: '#808080' };
const LINK: CSSProperties = { fontWeight: 700, color: '#000', fontSize: 12, textDecoration: 'none' };

function Caminho({ pergunta, onClick }: { pergunta: string; onClick: () => void }) {
  return (
    <button type="button" onClick={onClick} className="eva-caminho-de-conta">
      <span style={{ fontSize: 14, color: '#1E1E1E', textAlign: 'left' }}>{pergunta}</span>
      <span style={{ display: 'flex', alignItems: 'center', gap: 2, color: 'var(--eva-brand-accent-text)', fontSize: 14, fontWeight: 600, whiteSpace: 'nowrap' }}>
        Clique aqui
        <IconChevronRight size={16} />
      </span>
      <style>{`.eva-caminho-de-conta{display:flex;justify-content:space-between;align-items:center;gap:12px;width:100%;padding:14px 16px;background:#fff;border:1px solid #F1F1F1;border-radius:20px;cursor:pointer;font:inherit;transition:background-color 120ms ease}.eva-caminho-de-conta:hover{background-color:#F9F9F9}`}</style>
    </button>
  );
}
